import { useState, useRef, useEffect } from "react";
import { Play } from "lucide-react";

// Click-to-play video player.
// - The <video> element is only mounted after the user clicks, so the file
//   is NOT downloaded on initial page load (effectively lazy-loaded).
// - No autoplay-with-sound on load; playback is user-initiated.
// - Keeps the source aspect ratio (16:9) and matches the site's rounded card styling.
export function VideoPlayer({ src, poster, label, testid = "video-player" }) {
  const [active, setActive] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (active && videoRef.current) {
      const p = videoRef.current.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [active]);

  return (
    <div
      className="relative aspect-video w-full overflow-hidden rounded-2xl border border-border bg-charcoal"
      data-testid={testid}
    >
      {active ? (
        <video
          ref={videoRef}
          className="h-full w-full"
          src={src}
          poster={poster}
          controls
          playsInline
          preload="auto"
          aria-label={label}
          title={label}
          data-testid={`${testid}-video`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          aria-label={`Play video: ${label}`}
          data-testid={`${testid}-play`}
          className="group absolute inset-0 h-full w-full cursor-pointer"
        >
          <img
            src={poster}
            alt={label}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
          <span className="absolute inset-0 bg-charcoal/25 transition-colors duration-300 group-hover:bg-charcoal/15" />
          <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand/90 text-white shadow-[0_8px_30px_-6px_rgba(37,99,235,0.6)] ring-1 ring-white/20 transition-transform duration-300 group-hover:scale-110 sm:h-20 sm:w-20">
            <Play className="ml-1 h-7 w-7 fill-current sm:h-9 sm:w-9" strokeWidth={1.5} />
          </span>
        </button>
      )}
    </div>
  );
}
